import { BrowserRouter } from "react-router-dom";
import {
  MideyeAppAuthProvider,
  type MideyeAuthorize,
} from "@mideye/auth-react";
import App from "./App.tsx";
import { INSIGHT_APP_NAME, InsightAppIcon } from "./appIdentity.tsx";
import { api, clearAuthState, setTokenGetter } from "./services/api.ts";
import { AuthProvider, type UserInfo } from "./auth/AuthContext.tsx";
import { MOCK_AUTH_STORAGE_KEY } from "./auth/mockAuth.ts";

const authorizeInsightAccess: MideyeAuthorize<UserInfo> = ({ user }) => {
  // Access gate: membership in the Shield Insight Users group (resolved from Graph).
  if (!user.has_access) {
    return {
      reason: "not-authorized",
      title: "Access required",
      message:
        "Your account isn't a member of the Mideye Insight access group. Contact your administrator to request access.",
      requiredAccess: ["Shield Insight Users group"],
      supportHref: "mailto:support@mideye.com?subject=Mideye%20Insight%20access",
      supportLabel: "Request access",
    };
  }

  // Data scope: the user must be connected to a company.
  if (!user.company_id) {
    return {
      reason: "missing-company",
      title: "Company setup required",
      message: "This account is signed in but is not connected to a company.",
      requiredAccess: ["Company assignment"],
      supportHref: "mailto:support@mideye.com?subject=Mideye%20Insight%20access",
      supportLabel: "Request setup",
    };
  }

  return true;
};

export default function AppWithAuth() {
  return (
    <MideyeAppAuthProvider<UserInfo>
      appName={INSIGHT_APP_NAME}
      appIcon={<InsightAppIcon />}
      host={{
        getConfig: api.auth.getConfig,
        getMe: () => api.auth.getMe(),
        setTokenGetter,
        clearAuthState,
        clearBackendSession: api.auth.clearBackendSession,
      }}
      getUserEmail={(user) => user.email}
      authorize={authorizeInsightAccess}
      configCacheKey="insight_auth_config"
      mock={{
        storageKey: MOCK_AUTH_STORAGE_KEY,
        enabled: import.meta.env.DEV,
        validateUser: (raw): raw is UserInfo =>
          typeof raw === "object" &&
          raw !== null &&
          typeof (raw as UserInfo).user_id === "string",
      }}
    >
      <AuthProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AuthProvider>
    </MideyeAppAuthProvider>
  );
}
